"use strict";
(function () {
  if (!customElements.get("movements-videos")) {
    const CARET_SVG =
      '<svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';
    const PAGINATION_SELECTOR = "#pagination-";
    const VIDEO_SELECTOR = "#movement-video-";
    const VIDEO_SKELETON_SELECTOR = "#movement-video-skeleton-";
    const VIDEOS_DATA_SELECTOR = "#videos-data-";
    const REAL_PAGINATION_SELECTOR = "#mv-real-pagination";
    const TAGS_PARAM = "videos-tag";
    const PAGES_PARAM = "videos-page";

    class MovementsVideos extends HTMLElement {
      #sectionId = this.id.replace("videos-", "");
      #data = [];
      #allItemsCount = +this.dataset.allItemsCount;
      #itemsPerPage = +this.dataset.pagination || 6;
      #timeToUpdate = 300;

      // DOM elements
      #grid = null;
      #paginationContainer = null;
      #videoItemTemplate = null;
      #videoItemSkeletonTemplate = null;

      // Modal
      #modal = null;
      #modalContent = {
        videoContainer: null,
        title: null,
        badges: null,
        description: null,
      };
      #currentlyDisplayedItems = [];
      #currentModalIndex = -1;

      // Muted state tracking
      #isMuted = false;

      constructor() {
        super();
        this._onFilterChange = this._onFilterChange.bind(this);
        this._onPaginationClick = this._onPaginationClick.bind(this);
        this._handleGridClick = this._handleGridClick.bind(this);
        this._handleModalNav = this._handleModalNav.bind(this);
        this._handleModalClose = this._handleModalClose.bind(this);
        this._renderDescription = this._renderDescription.bind(this);
        this._updateModalContent = this._updateModalContent.bind(this);
      }

      connectedCallback() {
        // Show loading placeholder
        this.#showLoadingPlaceholder();

        this.#loadAllPaginatedData()
          .then(() => {
            this.#setupElements();
            this.#addEventListeners();
            this._initFromUrl();
            this.#hideLoadingPlaceholder();
          })
          .catch((error) => {
            console.error("❌ Failed to load video data:", error);
            this.#data = [];
            this.#setupElements();
            this.#addEventListeners();
            this._initFromUrl();
            this.#hideLoadingPlaceholder();
          });
      }

      /**
       * Show loading placeholders while fetching data
       */
      #showLoadingPlaceholder() {
        const grid = this.querySelector(`[data-component="grid"]`);
        if (!grid) return;

        grid.innerHTML = "";

        // Add loading message
        const loadingMessage = document.createElement("div");
        loadingMessage.style.cssText = `
          grid-column: 1 / -1;
          text-align: center;
          padding: 40px 20px;
          font-size: 16px;
          color: #666;
        `;
        loadingMessage.textContent = "Loading videos...";
        grid.appendChild(loadingMessage);

        const fragment = document.createDocumentFragment();

        // Show 6 skeleton loaders
        for (let i = 0; i < 6; i++) {
          const skeleton = document.createElement("div");
          skeleton.className = "skeleton-loader";
          skeleton.style.cssText = `
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: loading 1.5s infinite;
            height: 300px;
            border-radius: 8px;
          `;
          fragment.appendChild(skeleton);
        }

        grid.appendChild(fragment);

        // Add animation keyframes if not already present
        if (!document.querySelector("style[data-skeleton-animation]")) {
          const style = document.createElement("style");
          style.setAttribute("data-skeleton-animation", "true");
          style.textContent = `
            @keyframes loading {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `;
          document.head.appendChild(style);
        }
      }

      /**
       * Hide loading placeholders
       */
      #hideLoadingPlaceholder() {
        const grid = this.querySelector(`[data-component="grid"]`);
        if (grid) {
          grid.innerHTML = "";
        }
      }

      disconnectedCallback() {
        this.#removeEventListeners();
      }

      /**
       * Load all paginated data by fetching pages via their URLs
       * Extracts hrefs from pagination, fetches each page, extracts JSON
       */
      #loadAllPaginatedData() {
        return new Promise((resolve, reject) => {
          try {
            // Find the real pagination container
            const realPaginationContainer = this.querySelector(
              REAL_PAGINATION_SELECTOR,
            );

            // Extract all hrefs from pagination links
            const pageLinks = realPaginationContainer
              ? Array.from(realPaginationContainer.querySelectorAll("li > a"))
              : [];

            if (pageLinks.length === 0) {
              // Single page or no pagination — read data from inline <script>
              this.#loadLocalData();
              resolve();
              return;
            }

            // Use Set to get unique URLs (automatically deduplicates)
            const urlsSet = new Set();
            pageLinks.forEach((link) => {
              const href = link.href;
              if (href) {
                urlsSet.add(href);
              }
            });

            // Also add current page (page 1)
            urlsSet.add(window.location.href.split("?")[0]);

            const uniqueUrls = Array.from(urlsSet);

            // Create promises for fetching each page
            const fetchPromises = uniqueUrls.map((url, index) => {
              return new Promise((resolveItem) => {
                try {
                  fetch(url)
                    .then((response) => response.text())
                    .then((html) => {
                      // Parse HTML and extract JSON from script tags
                      const parser = new DOMParser();
                      const doc = parser.parseFromString(html, "text/html");

                      // Find all video data script tags
                      const scriptTags = doc.querySelectorAll(
                        `script[id^="videos-data-paginated-${this.#sectionId}-page-"]`,
                      );

                      let pageData = [];
                      if (scriptTags.length > 0) {
                        scriptTags.forEach((script) => {
                          try {
                            const data = JSON.parse(script.textContent);
                            pageData = pageData.concat(data);
                          } catch (e) {
                            console.warn("Failed to parse script data:", e);
                          }
                        });
                      }

                      resolveItem(pageData);
                    })
                    .catch((error) => {
                      console.warn(
                        `❌ Failed to fetch page ${index + 1}/${uniqueUrls.length}:`,
                        error,
                      );
                      resolveItem([]);
                    });
                } catch (e) {
                  console.warn(
                    `❌ Error fetching page ${index + 1}/${uniqueUrls.length}:`,
                    e,
                  );
                  resolveItem([]);
                }
              });
            });

            // Use Promise.all() to load all pages in parallel
            Promise.all(fetchPromises)
              .then((allPages) => {
                console.log(
                  `✓ All ${allPages.length} pages fetched successfully`,
                );

                // Combine all pages
                const combinedData = allPages.reduce(
                  (acc, pageData) => acc.concat(pageData),
                  [],
                );

                // Deduplicate based on title
                const seen = new Set();
                const deduplicatedData = combinedData.filter((item) => {
                  const key = item.title;
                  if (seen.has(key)) {
                    return false;
                  }
                  seen.add(key);
                  return true;
                });

                // Assign unique IDs
                this.#data = deduplicatedData.map((item, index) => ({
                  ...item,
                  uniqueId: index,
                }));

                const duplicatesRemoved =
                  combinedData.length - this.#data.length;
                // Check all unique tags from the deduplicated data
                const allTagsSet = new Set();
                const tagsByCount = {};
                const itemsByTag = {};

                this.#data.forEach((item) => {
                  if (item.tags && Array.isArray(item.tags)) {
                    item.tags.forEach((tag) => {
                      allTagsSet.add(tag);
                      tagsByCount[tag] = (tagsByCount[tag] || 0) + 1;

                      if (!itemsByTag[tag]) {
                        itemsByTag[tag] = [];
                      }
                      itemsByTag[tag].push(item.title);
                    });
                  }
                });

                const allTags = Array.from(allTagsSet).sort();
                allTags.forEach((tag) => {
                  const count = tagsByCount[tag];
                });

                // Verify tags are present
                if (allTags.length === 0) {
                  console.warn("⚠️ WARNING: No tags found in any items!");
                }

                resolve();
              })
              .catch((error) => {
                console.error(
                  "❌ Error loading pages with Promise.all:",
                  error,
                );
                reject(error);
              });
          } catch (error) {
            console.error("❌ Error in #loadAllPaginatedData:", error);
            reject(error);
          }
        });
      }

      /**
       * Fallback: read video data from inline <script> tags on the current page.
       * Used when there is only one page (no pagination links).
       * Prioritizes paginated script tags (inside {% paginate %} block, up to 250 items)
       * over the non-paginated one (which may be limited to 50 items).
       */
      #loadLocalData() {
        // 1. Try all paginated script tags first (inside {% paginate %} block)
        const paginatedScripts = this.querySelectorAll(
          `script[id^="videos-data-paginated-${this.#sectionId}-page-"]`,
        );

        if (paginatedScripts.length > 0) {
          let combinedData = [];
          paginatedScripts.forEach((script) => {
            try {
              const data = JSON.parse(script.textContent);
              combinedData = combinedData.concat(data);
            } catch (e) {
              console.warn("Failed to parse paginated video data:", e);
            }
          });

          if (combinedData.length > 0) {
            this.#data = combinedData.map((item, index) => ({
              ...item,
              uniqueId: index,
            }));
            console.log(
              `✅ Loaded ${this.#data.length} items from paginated data`,
            );
            return;
          }
        }

        // 2. Fallback to non-paginated script tag
        const fallbackEl = this.querySelector(
          `${VIDEOS_DATA_SELECTOR}${this.#sectionId}`,
        );
        if (fallbackEl?.textContent) {
          try {
            this.#data = JSON.parse(fallbackEl.textContent).map(
              (item, index) => ({
                ...item,
                uniqueId: index,
              }),
            );
            return;
          } catch (e) {
            console.warn("Failed to parse fallback video data:", e);
          }
        }

        this.#data = [];
      }

      #setupElements() {
        this.#grid = this.querySelector(`[data-component="grid"]`);
        this.#paginationContainer = this.querySelector(
          `${PAGINATION_SELECTOR}${this.#sectionId}`,
        );
        this.#videoItemTemplate = this.querySelector(
          `${VIDEO_SELECTOR}${this.#sectionId}`,
        );
        this.#videoItemSkeletonTemplate = this.querySelector(
          `${VIDEO_SKELETON_SELECTOR}${this.#sectionId}`,
        );

        this.#modal = this.querySelector(
          `custom-modal[data-id="modal-${this.#sectionId}"]`,
        );
        if (this.#modal) {
          this.#modalContent.videoContainer = this.#modal.querySelector(
            '[data-component="video-container"]',
          );
          this.#modalContent.title = this.#modal.querySelector(
            '[data-component="video-title"]',
          );
          this.#modalContent.badges = this.#modal.querySelector(
            '[data-component="video-badges"]',
          );
          this.#modalContent.description = this.#modal.querySelector(
            '[data-component="video-description"]',
          );
        }

        // Update filter tags after setup
        this.#updateFilterTags();
      }

      /**
       * Hide missing tags and add newly discovered tags to filter
       */
      #updateFilterTags() {
        const filterContainer = this.querySelector(
          "movements-video-filters[data-component='tags']",
        );

        if (!filterContainer) {
          console.warn("⚠️ Filter container not found");
          return;
        }

        // Get all unique tags from loaded data

        const allTagsSet = new Set();
        console.log(this);
        this.#data.forEach((item) => {
          if (item.tags && Array.isArray(item.tags)) {
            item.tags.forEach((tag) => allTagsSet.add(tag));
          }
        });

        const allTags = Array.from(allTagsSet).sort();

        // Get existing filter buttons from Liquid
        const existingButtons = filterContainer.querySelectorAll(
          ".movements-video-filters__tag",
        );
        const existingTags = new Set(
          Array.from(existingButtons).map((btn) => btn.dataset.value),
        );

        // Find tags that exist in data but not in Liquid
        const missingTags = [];
        allTags.forEach((tag) => {
          if (!existingTags.has(tag)) {
            missingTags.push(tag);
          }
        });

        if (missingTags.length > 0) {
          // Create buttons for missing tags
          missingTags.forEach((tag) => {
            const button = document.createElement("button");
            button.className = "movements-video-filters__tag";
            button.dataset.value = tag;
            button.textContent = tag;
            filterContainer.appendChild(button);
          });
        }

        // Hide buttons for tags that have no items (shouldn't happen, but safety check)
        const tagsWithItems = new Set(allTags);
        const buttonsToRemove = [];

        existingButtons.forEach((btn) => {
          const tag = btn.dataset.value;
          if (!tagsWithItems.has(tag)) {
            console.warn(`🚫 Tag with no items: ${tag}`);
            // btn.style.display = "none";
            // buttonsToRemove.push(btn);
          }
        });

        // Remove the hidden buttons from DOM entirely
        buttonsToRemove.forEach((btn) => btn.remove());
      }

      #addEventListeners() {
        this.addEventListener("filter-change", this._onFilterChange);

        if (this.#paginationContainer) {
          this.#paginationContainer.addEventListener(
            "click",
            this._onPaginationClick,
          );
        }

        if (this.#grid) {
          this.#grid.addEventListener("click", this._handleGridClick);
        }

        if (this.#modal) {
          this.#modal.addEventListener("click", this._handleModalNav);
          this.#modal
            .querySelector("dialog")
            .addEventListener("close", this._handleModalClose);
        }
      }

      #removeEventListeners() {
        this.removeEventListener("filter-change", this._onFilterChange);
        if (this.#paginationContainer) {
          this.#paginationContainer.removeEventListener(
            "click",
            this._onPaginationClick,
          );
        }
        if (this.#grid) {
          this.#grid.removeEventListener("click", this._handleGridClick);
        }
        if (this.#modal) {
          this.#modal.removeEventListener("click", this._handleModalNav);
          this.#modal
            .querySelector("dialog")
            .removeEventListener("close", this._handleModalClose);
        }
      }

      #skeletonRender() {
        if (!this.#grid || !this.#videoItemSkeletonTemplate) {
          return;
        }
        this.#grid.innerHTML = "";
        const fragment = document.createDocumentFragment();
        const skeletonCount = Math.min(
          this.#itemsPerPage,
          this.#data.length || this.#itemsPerPage,
        );
        for (let i = 0; i < skeletonCount; i++) {
          const skeletonNode =
            this.#videoItemSkeletonTemplate.content.cloneNode(true);
          fragment.appendChild(skeletonNode);
        }
        this.#grid.appendChild(fragment);
      }

      _initFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const activeTag = params.get(TAGS_PARAM);
        const tagButton = this.querySelector(
          `[data-value="${activeTag || "all"}"]`,
        );
        if (tagButton) {
          tagButton.classList.add("active");
        }
        this._render();
      }

      _render() {
        this.#skeletonRender();
        setTimeout(() => {
          this.#currentlyDisplayedItems = this._getFilteredItems();

          const currentPage = this._getCurrentPage();
          const paginatedItems = this.#currentlyDisplayedItems.slice(
            (currentPage - 1) * this.#itemsPerPage,
            currentPage * this.#itemsPerPage,
          );
          this.#grid.innerHTML = this._generateGridHTML(paginatedItems);
          if (this.#paginationContainer) {
            this.#paginationContainer.innerHTML = this._generatePaginationHTML(
              this.#currentlyDisplayedItems.length,
              currentPage,
            );
          }
        }, this.#timeToUpdate);
      }

      _onFilterChange() {
        const activeTag = this._getActiveTag();
        const params = new URLSearchParams();
        if (activeTag) {
          params.set(TAGS_PARAM, activeTag);
        }
        this._updateUrl(params);
        this._render();
      }

      _onPaginationClick(event) {
        event.preventDefault();
        const link = event.target.closest("a");
        if (!link || link.parentElement.classList.contains("disabled")) return;

        const url = new URL(link.href, window.location.origin);
        const params = new URLSearchParams(url.search);
        this._updateUrl(params);
        this._render();
      }

      _getActiveTag() {
        const activeTagEl = this.querySelector(
          ".movements-video-filters__tag.active",
        );
        if (activeTagEl && activeTagEl.dataset.value !== "all") {
          return activeTagEl.dataset.value;
        }
        return null;
      }

      _getFilteredItems() {
        const activeTag = this._getActiveTag();
        if (!activeTag) {
          return this.#data;
        }
        return this.#data.filter(
          (item) => item.tags && item.tags.includes(activeTag),
        );
      }

      _getCurrentPage() {
        const params = new URLSearchParams(window.location.search);
        return parseInt(params.get(PAGES_PARAM) || "1", 10);
      }

      _updateUrl(params) {
        const url = `?${params.toString()}`;
        history.pushState({ path: url }, "", url);
      }

      _generateGridHTML(items) {
        if (items.length === 0) {
          return '<p class="movements-videos__empty-message">No videos match your selection.</p>';
        }
        return items.map((item) => this._renderVideoItem(item)).join("");
      }

      _renderVideoItem(item) {
        if (!this.#videoItemTemplate) return "";
        const clone = this.#videoItemTemplate.content.cloneNode(true);

        const opener = clone.querySelector("custom-modal-opener");
        if (opener) {
          opener.dataset.uniqueId = item.uniqueId;
        }

        const titleEl = clone.querySelector('[data-compoent="title"]');
        if (titleEl) titleEl.textContent = item.title || "";

        const thumbnailEl = clone.querySelector(
          '[data-component="video-thumbnail"]',
        );
        if (thumbnailEl && item.poster_url) {
          thumbnailEl.style.backgroundImage = `url('${item.poster_url}')`;
        }

        const badgesContainer = clone.querySelector(
          '[data-component="badges"]',
        );
        const badgeTemplate = clone.querySelector('[data-component="badge"]');

        if (badgesContainer && badgeTemplate) {
          if (item.badges && item.badges.length > 0) {
            badgesContainer.innerHTML = "";

            item.badges.forEach((badgeText) => {
              const newBadge = badgeTemplate.cloneNode(true);
              newBadge.textContent = badgeText;
              badgesContainer.appendChild(newBadge);
            });
          } else {
            badgesContainer.innerHTML = "";
          }
        }

        const tempDiv = document.createElement("div");
        tempDiv.appendChild(clone);
        return tempDiv.innerHTML;
      }

      _generatePaginationHTML(totalItems, currentPage) {
        const totalPages = Math.ceil(totalItems / this.#itemsPerPage);
        if (totalPages <= 1) {
          return "";
        }

        let html = '<nav class="pagination">';
        const padding = 2;

        const prevDisabled = currentPage === 1 ? "disabled" : "";
        html += `<span class="pagination__item pagination__item--arrow ${prevDisabled}"><a href="${this._getPageUrl(
          currentPage - 1,
        )}">${CARET_SVG}</a></span>`;

        let hasRenderedStartEllipsis = false;
        let hasRenderedEndEllipsis = false;

        for (let i = 1; i <= totalPages; i++) {
          const isFirstPage = i === 1;
          const isLastPage = i === totalPages;
          const isInPaddingRange =
            i >= currentPage - padding && i <= currentPage + padding;

          if (isFirstPage || isLastPage || isInPaddingRange) {
            const activeClass =
              i === currentPage ? "pagination__item--current" : "";
            html += `<span class="pagination__item ${activeClass}"><a href="${this._getPageUrl(
              i,
            )}">${i}</a></span>`;
          } else if (i < currentPage && !hasRenderedStartEllipsis) {
            html +=
              '<span class="pagination__item pagination__item--dots">...</span>';
            hasRenderedStartEllipsis = true;
          } else if (i > currentPage && !hasRenderedEndEllipsis) {
            html +=
              '<span class="pagination__item pagination__item--dots">...</span>';
            hasRenderedEndEllipsis = true;
          }
        }

        const nextDisabled = currentPage === totalPages ? "disabled" : "";
        html += `<span class="pagination__item pagination__item--arrow ${nextDisabled}"><a href="${this._getPageUrl(
          currentPage + 1,
        )}">${CARET_SVG}</a></span>`;

        html += "</nav>";
        return html;
      }

      _getPageUrl(pageNumber) {
        const activeTag = this._getActiveTag();
        const params = new URLSearchParams();
        if (activeTag) {
          params.set(TAGS_PARAM, activeTag);
        }
        if (pageNumber > 0) {
          params.set(PAGES_PARAM, pageNumber);
        }
        return `?${params.toString()}`;
      }

      _handleGridClick(event) {
        let opener = event.target.closest("custom-modal-opener");
        if (event.target.classList.contains("custom-modal-opener")) {
          opener = event.target;
        }

        if (!opener || !this.#modal) return;

        const uniqueId = parseInt(opener.dataset.uniqueId, 10);

        this.#currentModalIndex = this.#currentlyDisplayedItems.findIndex(
          (item) => item.uniqueId === uniqueId,
        );

        if (this.#currentModalIndex === -1) return;

        const dataItem = this.#currentlyDisplayedItems[this.#currentModalIndex];

        this._updateModalContent(dataItem);
        this.#modal.open();
      }

      _handleModalNav(event) {
        const button = event.target.closest("button[data-type]");
        if (!button) return;

        const direction = button.dataset.type;

        if (direction === "next") {
          this.#currentModalIndex++;
          if (this.#currentModalIndex >= this.#currentlyDisplayedItems.length) {
            this.#currentModalIndex = 0;
          }
        } else if (direction === "prev") {
          this.#currentModalIndex--;
          if (this.#currentModalIndex < 0) {
            this.#currentModalIndex = this.#currentlyDisplayedItems.length - 1;
          }
        }

        const newItem = this.#currentlyDisplayedItems[this.#currentModalIndex];
        this._updateModalContent(newItem);
      }

      _updateModalContent(item) {
        if (!item || !this.#modal) return;

        this.#modalContent.title.textContent = item.title;
        const oldVideo =
          this.#modalContent.videoContainer.querySelector("video");

        if (oldVideo) {
          const mutedBtn = this.querySelector("video-mute-btn");
          if (mutedBtn) {
            this.#isMuted =
              oldVideo.muted || mutedBtn.classList.contains("is-muted");
          }
          oldVideo.remove();
        }

        const oldPoster = this.#modalContent.videoContainer.querySelector(
          ".movement-videos-modal__poster",
        );
        if (oldPoster) {
          oldPoster.remove();
        }

        const posterUrl =
          item.poster_url || item.video?.preview_image?.src || "";
        if (posterUrl) {
          const imgAlt = item.title || "Image";
          const posterHTML = `<img src="${posterUrl}" alt="${imgAlt}" class="movement-videos-modal__poster">`;
          this.#modalContent.videoContainer.insertAdjacentHTML(
            "afterbegin",
            posterHTML,
          );
        }

        const videoURL = item?.video_url || "";
        const videoHTML = this._renderVideoForModal(item.video, videoURL);
        this.#modalContent.videoContainer.insertAdjacentHTML(
          "beforeend",
          videoHTML,
        );

        this._attachVideoEventListeners();
        this._applyMutedState();

        this.#modalContent.description.innerHTML = this._renderDescription(
          item.description,
        );
        const colorScheme = this.dataset.badgeColorScheme || "scheme-1";

        this.#modalContent.badges.innerHTML = item.badges
          .map((b) => `<div class="badge color-${colorScheme}">${b}</div>`)
          .join("");
      }

      _applyMutedState() {
        const video = this.#modalContent.videoContainer?.querySelector("video");
        if (!video) return;

        if (this.#isMuted) {
          video.muted = true;
          video.classList.add("is-muted");
        } else {
          video.muted = false;
          video.classList.remove("is-muted");
        }
      }

      _renderVideoForModal(video, videoURL) {
        // If we have a direct video URL, use that
        if (videoURL) {
          const videoId = `modal-video-${this.#sectionId}`;
          const poster = video?.preview_image?.src || video?.poster_url || "";
          return `<video id="${videoId}" playsinline autoplay controls src="${videoURL}" poster="${poster}"></video>`;
        }

        // Otherwise, try to use the video object with sources
        if (!video || !video.sources) return "";

        const videoId = `modal-video-${this.#sectionId}`;
        const poster = video.preview_image?.src || "";
        const sources = video.sources
          .filter((s) => s.format === "mp4")
          .map(
            (s) =>
              `<source src="${s.url}" type="video/mp4" height="${s.height}">`,
          )
          .join("");

        return `<video id="${videoId}" playsinline autoplay controls poster="${poster}">${sources}</video>`;
      }

      _renderDescription(description) {
        if (!description || !description.children) return "";
        return this._renderChildren(description.children);
      }

      _renderChildren(children) {
        if (!children || !Array.isArray(children)) return "";

        return children.map((child) => this._renderElement(child)).join("");
      }

      _renderElement(element) {
        if (!element) return "";

        if (element.type === "text") {
          return this._escapeHtml(element.value || "");
        }

        const innerContent = this._renderChildren(element.children);

        switch (element.type) {
          case "paragraph":
            return `<p>${innerContent}</p>`;

          case "heading":
            const level = element.level || 1;
            const tag = `h${Math.min(Math.max(level, 1), 6)}`;
            return `<${tag}>${innerContent}</${tag}>`;

          case "heading-1":
            return `<h1>${innerContent}</h1>`;
          case "heading-2":
            return `<h2>${innerContent}</h2>`;
          case "heading-3":
            return `<h3>${innerContent}</h3>`;
          case "heading-4":
            return `<h4>${innerContent}</h4>`;
          case "heading-5":
            return `<h5>${innerContent}</h5>`;
          case "heading-6":
            return `<h6>${innerContent}</h6>`;

          case "list":
          case "unordered-list":
          case "ul":
            return `<ul>${innerContent}</ul>`;

          case "ordered-list":
          case "ol":
            return `<ol>${innerContent}</ol>`;

          case "list-item":
          case "li":
            return `<li>${innerContent}</li>`;

          case "strong":
          case "bold":
            return `<strong>${innerContent}</strong>`;

          case "emphasis":
          case "em":
          case "italic":
            return `<em>${innerContent}</em>`;

          case "link":
            const href = this._escapeHtml(element.url || element.href || "#");
            const target = element.target
              ? ` target="${this._escapeHtml(element.target)}"`
              : "";
            const rel =
              element.target === "_blank" ? ' rel="noopener noreferrer"' : "";
            return `<a href="${href}"${target}${rel}>${innerContent}</a>`;

          case "code":
            return `<code>${this._escapeHtml(innerContent)}</code>`;

          case "code-block":
            const language = element.language
              ? ` class="language-${this._escapeHtml(element.language)}"`
              : "";
            return `<pre><code${language}>${this._escapeHtml(
              innerContent,
            )}</code></pre>`;

          case "blockquote":
            return `<blockquote>${innerContent}</blockquote>`;

          case "line-break":
          case "br":
            return "<br>";

          case "horizontal-rule":
          case "hr":
            return "<hr>";

          case "image":
            const src = this._escapeHtml(element.src || element.url || "");
            const alt = this._escapeHtml(
              element.alt || element.title || "Image",
            );
            const title = element.title
              ? ` title="${this._escapeHtml(element.title)}"`
              : "";
            return `<img src="${src}" alt="${alt}"${title}>`;

          default:
            return innerContent;
        }
      }

      _escapeHtml(text) {
        if (typeof text !== "string") return "";

        const map = {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#039;",
        };

        return text.replace(/[&<>"']/g, (char) => map[char]);
      }

      _attachVideoEventListeners() {
        const video = this.#modalContent.videoContainer.querySelector("video");
        if (!video) return;

        const handlePlay = () => {
          this.#modalContent.videoContainer.classList.add("is-playing");
          const playBtn =
            this.#modalContent.videoContainer.querySelector("video-play-btn");
          if (playBtn) {
            playBtn.classList.add("is-playing");
          }
        };

        const handlePause = () => {
          this.#modalContent.videoContainer.classList.remove("is-playing");
          const playBtn =
            this.#modalContent.videoContainer.querySelector("video-play-btn");
          if (playBtn) {
            playBtn.classList.remove("is-playing");
          }
        };

        const handleEnded = () => {
          this.#modalContent.videoContainer.classList.remove("is-playing");
          const playBtn =
            this.#modalContent.videoContainer.querySelector("video-play-btn");
          if (playBtn) {
            playBtn.classList.remove("is-playing");
          }
        };

        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
        video.removeEventListener("ended", handleEnded);

        video.addEventListener("play", handlePlay);
        video.addEventListener("pause", handlePause);
        video.addEventListener("ended", handleEnded);
      }

      _handleModalClose() {
        const video = this.#modalContent.videoContainer?.querySelector("video");
        if (video) {
          video.pause();
          video.currentTime = 0;
          this.#modalContent.videoContainer.classList.remove("is-playing");
        }
      }
    }

    customElements.define("movements-videos", MovementsVideos);
  }

  if (!customElements.get("movements-video-filters")) {
    const DRAG_SCROLL_MULTIPLIER = 1.5;
    const DRAG_THRESHOLD_PX = 5;
    const ACTIVE_TAG_CLASS = "active";
    const TAG_SELECTOR = ".movements-video-filters__tag";

    class MovementsVideoFilters extends HTMLElement {
      #isDragging = false;
      #isPointerDown = false;
      #startX = 0;
      #scrollLeft = 0;

      connectedCallback() {
        this.style.cursor = "grab";
        this.#addEventListeners();
      }

      disconnectedCallback() {
        this.#removeEventListeners();
      }

      #addEventListeners() {
        this.addEventListener("pointerdown", this.#handlePointerDown);
        this.addEventListener("pointermove", this.#handlePointerMove);
        this.addEventListener("pointerup", this.#handlePointerUp);
        this.addEventListener("pointerleave", this.#handlePointerLeave);
        this.addEventListener("click", this.#handleClick);
      }

      #removeEventListeners() {
        this.removeEventListener("pointerdown", this.#handlePointerDown);
        this.removeEventListener("pointermove", this.#handlePointerMove);
        this.removeEventListener("pointerup", this.#handlePointerUp);
        this.removeEventListener("pointerleave", this.#handlePointerLeave);
        this.removeEventListener("click", this.#handleClick);
      }

      #handlePointerDown = (event) => {
        if (event.button !== 0) return;

        this.#isPointerDown = true;
        this.style.cursor = "grabbing";
        this.classList.add("is-dragging");

        this.#startX = event.pageX - this.offsetLeft;
        this.#scrollLeft = this.scrollLeft;

        this.style.scrollSnapType = "none";
        event.preventDefault();
      };

      #handlePointerLeave = () => {
        this.#isPointerDown = false;
        this.classList.remove("is-dragging");
        this.style.cursor = "grab";
        this.style.scrollSnapType = "";
      };

      #handlePointerUp = () => {
        this.#isPointerDown = false;
        this.classList.remove("is-dragging");
        this.style.cursor = "grab";
        this.style.scrollSnapType = "";

        setTimeout(() => {
          this.#isDragging = false;
        }, 0);
      };

      #handlePointerMove = (event) => {
        if (!this.#isPointerDown) return;
        event.preventDefault();

        const x = event.pageX - this.offsetLeft;
        const walk = (x - this.#startX) * DRAG_SCROLL_MULTIPLIER;

        if (Math.abs(walk) > DRAG_THRESHOLD_PX) {
          this.#isDragging = true;
        }

        this.scrollLeft = this.#scrollLeft - walk;
      };

      #handleClick = (event) => {
        if (this.#isDragging) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        const clickedButton = event.target.closest(TAG_SELECTOR);
        if (!clickedButton) return;

        this.querySelectorAll(TAG_SELECTOR).forEach((tag) =>
          tag.classList.remove(ACTIVE_TAG_CLASS),
        );
        clickedButton.classList.add(ACTIVE_TAG_CLASS);

        this.dispatchEvent(
          new CustomEvent("filter-change", { bubbles: true, composed: true }),
        );
      };
    }

    customElements.define("movements-video-filters", MovementsVideoFilters);
  }
})();
